import {observer} from 'mobx-react-lite'
import React, {
  CSSProperties,
  UIEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ffColumnVirt, hasFeatureFlag, logger} from 'yougile-util'
import {type DataObjectId, Hub} from 'yougile-model'
import {BoardScrollStorage} from '@client/board-scroll-storage'
import {addingAllowed} from '@client/hub-actions-allowed'
import {ColumnChooser} from '@components/column-chooser'
import {Column, ColumnAdd} from '@components/columns'
import {BoardButton} from '@components/common/base/buttons'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useLoggedInStore} from '@components/logged-in/logged-in-store'
import {MultiActionsWithTasks} from '@components/multi-actions-with-tasks/multi-actions-with-tasks'
import {
  MultiActionsWithTasksContext as MultiContext,
  multiActionsWithTasksStore as multiStore
} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {stickerPanelGlobalStore} from '@components/sticker/sticker-panel/sticker-panel-global-store'
import {WorkflowCreate} from '@components/workflow/workflow-create'
import {useDelayedEffect, useSyncEvent} from '@containers/hooks'
import {useBoardStore} from './board-store'
import {moreThanOneBoard} from './board-utils'
import {useInitTaskIds} from './use-init-task-ids'
import {useUpdateBackground} from './use-update-background'
import {useVirtualItems} from './use-virtual-items'

interface BoardProps {
  board: Hub
}

export const Board = (props: BoardProps) => {
  useInitTaskIds(props.board)

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('board error ' + e.message)}
    >
      <MultiContext.Provider value={multiStore}>
        <BoardBase {...props} />
      </MultiContext.Provider>
    </ErrorBoundary>
  )
}

const BoardBase = observer(({board}: BoardProps) => {
  const [state, store] = useBoardStore()

  const withVirtualization = hasFeatureFlag(ffColumnVirt)

  useLayoutEffect(() => {
    store.syncOnNew(board)
  }, [board])

  useSyncEvent(
    'current',
    {filter: board?.id, debounceTime: 50},
    () => {
      store.syncOnUpdate()
    },
    [board]
  )

  useUpdateBackground(board)

  useEffect(() => {
    store.updateColumnHeight()
  }, [])

  useDelayedEffect(
    store.updateColumnHeight,
    [
      stickerPanelGlobalStore.expanded,
      stickerPanelGlobalStore.state.oneLineMaxHeight,
      state.currentGroups.length
    ],
    100
  )

  return (
    <>
      <WorkflowCreate boardId={board.id} />
      {withVirtualization && <VirtualizedColumns board={board} />}
      {!withVirtualization && (
        <Columns
          board={board}
          onScroll={() => {
            state.tasksRenderQueue.requestToUpdatePriorities()
            state.columnsRenderQueue.requestToUpdatePriorities()
          }}
          columns={state.currentGroups
            .filter(column => column.location === board.id)
            .map(column => ({column}))}
          className="inline-flex space-x-8"
        />
      )}
    </>
  )
})

const VirtualizedColumns = observer(({board}: BoardProps) => {
  const [state] = useBoardStore()
  const {onScroll, setContainerEl, virtualItems} = useVirtualItems(board)

  const columns = virtualItems.map(item => ({
    column: state.currentGroups[item.index],
    style: {
      position: 'absolute',
      left: '0',
      transform: `translateX(${item.offset}px)`
    } as CSSProperties
  }))

  return (
    <Columns
      board={board}
      onScroll={onScroll}
      setContainerEl={setContainerEl}
      columns={columns}
    />
  )
})

const Columns = observer(
  ({
    board,
    onScroll,
    columns,
    setContainerEl,
    className
  }: BoardProps & {
    onScroll: UIEventHandler<HTMLElement>
    columns: {column: Hub; style?: CSSProperties}[]
    setContainerEl?: (el: HTMLElement | null) => void
    className?: string
  }) => {
    const [state, store] = useBoardStore()
    const [loggedInState] = useLoggedInStore()
    const scrollContainerRef = useRef<HTMLElement | null>(null)

    const handleContainerRef = useCallback(
      (el: HTMLElement | null) => {
        scrollContainerRef.current = el
        setContainerEl?.(el)
      },
      [setContainerEl]
    )

    const handleScroll: UIEventHandler<HTMLElement> = useCallback(
      event => {
        onScroll(event)
        if (!setContainerEl) {
          const target = event.target as HTMLElement
          BoardScrollStorage.saveScrollPosition(board.id, target.scrollLeft)
        }
      },
      [onScroll, board.id, setContainerEl]
    )

    useEffect(() => {
      const el = scrollContainerRef.current
      const canRestore = board.id && !setContainerEl && el && !state.loading
      canRestore && restoreScrollPosition(el, board.id)
    }, [board.id, state.loading])

    useEffect(() => {
      store.updateColumnHeight()
    }, [loggedInState?.scriptsOpen])

    return (
      <WithPerfectScrollbar
        className="proj-task-list flex-1"
        onMouseDown={store.boardDragScroll()}
        scrollDirection="x"
        wheelSpeed={1.5}
        onScroll={handleScroll}
        containerRef={handleContainerRef}
      >
        {!state.loading && (
          <TwTaskList
            style={{width: `${store.boardWidth}px`}}
            className={className}
          >
            {columns.map(col => (
              <Column
                key={col.column.id}
                board={board}
                column={col.column}
                style={col.style}
              />
            ))}

            <AddToBoard board={board} />

            {!state.columnShowAdd && <MultiActionsWithTasks />}
          </TwTaskList>
        )}
      </WithPerfectScrollbar>
    )
  }
)

export const AddToBoard = observer(({board}: {board: Hub}) => {
  const [state, store] = useBoardStore()
  const mirrorRef = useRef(null)
  const isMoreThanOneBoard = moreThanOneBoard()

  if (!addingAllowed(board?.id)) {
    return null
  }

  return (
    <div className="task-list-add-group w-192 space-y-4 ml-auto mr-112">
      {!state.columnShowAdd && (
        <TwBoardButton
          icon="IconCirclePlus"
          title={Lang.msg.createColumn}
          onClick={() => {
            store.setBoardShowAdd(true)
            store.setBoardShowAddExistent(false)
          }}
        />
      )}

      {state.columnShowAdd && <ColumnAdd />}

      {!state.columnShowAdd && isMoreThanOneBoard && (
        <TwBoardButton
          icon="IconClipboardList"
          title={Lang.msg.boardCreateDashboard}
          onClick={() => store.addReport()}
        />
      )}

      {!state.columnShowAdd && isMoreThanOneBoard && (
        <TwBoardButton
          ref={mirrorRef}
          icon="IconCustomMirror"
          disabled={state.columnShowAdd}
          title={
            !state.columnShowAddExistent
              ? Lang.msg.addMirror
              : Lang.msg.chooseColumns
          }
          onClick={() => {
            store.setBoardShowAdd(false)
            store.toggleBoardShowAddExistent()
          }}
        />
      )}

      {!state.columnShowAdd && state.columnShowAddExistent && (
        <Dropdown
          reference={mirrorRef}
          content={
            <ColumnChooser
              onChoose={store.onMoveColumnChoose}
              className="!mb-0"
            />
          }
          visible
          onVisibleChange={() => store.setBoardShowAddExistent(false)}
        />
      )}
    </div>
  )
})

const TwTaskList = tw.div`
  task-list
  relative
  overflow-hidden
  h-auto
`

const TwBoardButton = tw(BoardButton)`
  !w-full
  !justify-start
  !font-semibold
  !bg-column-bg-gray
`

const restoreScrollPosition = (element: HTMLElement, boardId: DataObjectId) => {
  const savedPosition = BoardScrollStorage.getScrollPosition(boardId)
  requestAnimationFrame(() => {
    element.scroll({left: savedPosition, behavior: 'auto'})
  })
}
